import React, { useEffect, useRef } from 'react';

const ACCENT = { r: 122, g: 163, b: 193 };
const DIRECTIONS: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1]];

interface Trace {
  segments: { x1: number; y1: number; x2: number; y2: number }[];
  progress: number;       // 0→1 how far the current segment has drawn
  segIndex: number;       // which segment is currently drawing
  speed: number;
  done: boolean;
  glow: number;           // pulse phase
  nodeRadius: number;
}

interface Node {
  x: number; y: number;
  pulse: number;
  radius: number;
  born: number;
}

function buildTrace(startX: number, startY: number, w: number, h: number): Trace {
  const segments: Trace['segments'] = [];
  let x = startX;
  let y = startY;
  const numSegments = 3 + Math.floor(Math.random() * 5);

  for (let i = 0; i < numSegments; i++) {
    const dir = DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)];
    const len = 40 + Math.random() * 120;
    const nx = Math.min(Math.max(x + dir[0] * len, 20), w - 20);
    const ny = Math.min(Math.max(y + dir[1] * len, 20), h - 20);
    segments.push({ x1: x, y1: y, x2: nx, y2: ny });
    x = nx;
    y = ny;
  }

  return { segments, progress: 0, segIndex: 0, speed: 0.01 + Math.random() * 0.02, done: false, glow: Math.random() * Math.PI * 2, nodeRadius: 2 + Math.random() * 2 };
}

const CircuitCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const traces = useRef<Trace[]>([]);
  const nodes = useRef<Node[]>([]);
  const animationId = useRef<number>(0);
  const frameCount = useRef(0);
  const mouse = useRef({ x: -1000, y: -1000 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      traces.current = [];
      nodes.current = [];
    };
    resize();
    window.addEventListener('resize', resize);

    const handleMouse = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    canvas.addEventListener('mousemove', handleMouse);

    const draw = () => {
      const { width: w, height: h } = canvas;
      frameCount.current++;

      // Fade previous frame for trail effect
      ctx.fillStyle = 'rgba(10, 10, 10, 0.03)';
      ctx.fillRect(0, 0, w, h);

      // Spawn new traces periodically
      if (frameCount.current % 60 === 0 && traces.current.filter(t => !t.done).length < 8) {
        const sx = Math.random() * w;
        const sy = Math.random() * h;
        traces.current.push(buildTrace(sx, sy, w, h));
      }

      // Spawn trace near mouse on movement
      if (frameCount.current % 90 === 0 && mouse.current.x > 0) {
        const mx = mouse.current.x + (Math.random() - 0.5) * 100;
        const my = mouse.current.y + (Math.random() - 0.5) * 100;
        traces.current.push(buildTrace(mx, my, w, h));
      }

      // Draw and advance traces
      for (const trace of traces.current) {
        if (trace.done) continue;
        trace.glow += 0.02;

        // Draw completed segments
        for (let i = 0; i < trace.segIndex; i++) {
          const seg = trace.segments[i];
          const alpha = 0.15 + 0.1 * Math.sin(trace.glow + i);
          ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${alpha})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(seg.x1, seg.y1);
          ctx.lineTo(seg.x2, seg.y2);
          ctx.stroke();
        }

        // Draw current segment (partially)
        if (trace.segIndex < trace.segments.length) {
          const seg = trace.segments[trace.segIndex];
          const cx = seg.x1 + (seg.x2 - seg.x1) * trace.progress;
          const cy = seg.y1 + (seg.y2 - seg.y1) * trace.progress;

          // Trace line
          ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},0.5)`;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(seg.x1, seg.y1);
          ctx.lineTo(cx, cy);
          ctx.stroke();

          // Glowing tip
          const glowAlpha = 0.6 + 0.4 * Math.sin(frameCount.current * 0.1);
          ctx.shadowColor = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${glowAlpha})`;
          ctx.shadowBlur = 8;
          ctx.fillStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},0.9)`;
          ctx.beginPath();
          ctx.arc(cx, cy, 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;

          trace.progress += trace.speed;
          if (trace.progress >= 1) {
            trace.progress = 0;
            // Add a node at the junction
            nodes.current.push({ x: seg.x2, y: seg.y2, pulse: 0, radius: trace.nodeRadius, born: frameCount.current });
            trace.segIndex++;
            if (trace.segIndex >= trace.segments.length) {
              trace.done = true;
            }
          }
        }
      }

      // Draw nodes (solder points)
      for (let i = nodes.current.length - 1; i >= 0; i--) {
        const node = nodes.current[i];
        node.pulse += 0.03;
        const age = frameCount.current - node.born;
        const fadeAlpha = Math.max(0, 1 - age / 400);

        if (fadeAlpha <= 0) {
          nodes.current.splice(i, 1);
          continue;
        }

        const pulseR = node.radius + Math.sin(node.pulse) * 1;

        // Outer glow
        ctx.shadowColor = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${0.5 * fadeAlpha})`;
        ctx.shadowBlur = 6;
        ctx.fillStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${0.3 * fadeAlpha})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, pulseR + 2, 0, Math.PI * 2);
        ctx.fill();

        // Inner dot
        ctx.shadowBlur = 0;
        ctx.fillStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${0.8 * fadeAlpha})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, pulseR, 0, Math.PI * 2);
        ctx.fill();
      }

      // Clean up old completed traces
      if (traces.current.length > 20) {
        traces.current = traces.current.filter(t => !t.done || nodes.current.some(n => {
          const last = t.segments[t.segments.length - 1];
          return last && Math.abs(n.x - last.x2) < 1 && Math.abs(n.y - last.y2) < 1;
        }));
      }

      animationId.current = requestAnimationFrame(draw);
    };

    // Initial clear
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    animationId.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animationId.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouse);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full z-0" />;
};

export default CircuitCanvas;
